/**
 * Runtime Environment Detector
 * 
 * Detects runtime environment (browser, node, vscode, standalone)
 */

import type { Environment } from './types';

export class EnvironmentDetector {
  private _isVSCode: boolean;
  private _isStandalone: boolean;
  private _isBrowser: boolean;
  private _isNode: boolean;

  constructor() {
    this._isVSCode = this.checkVSCodeAPI();
    this._isBrowser = this.checkBrowser();
    this._isNode = this.checkNode();
    this._isStandalone = !this._isVSCode && this._isBrowser && !this._isNode;
  }

  private checkVSCodeAPI(): boolean {
    if (typeof window === 'undefined') return false;
    return typeof (window as any).vscode !== 'undefined';
  }

  private checkBrowser(): boolean {
    if (typeof window === 'undefined') return false;
    return typeof (window as any).navigator !== 'undefined';
  }

  private checkNode(): boolean {
    if (typeof process === 'undefined') return false;
    return !!(process.versions && process.versions.node);
  }

  get isVSCode(): boolean { return this._isVSCode; }
  get isStandalone(): boolean { return this._isStandalone; }
  get isBrowser(): boolean { return this._isBrowser; }
  get isNode(): boolean { return this._isNode; }

  getEnvironment(): Environment {
    const nodeEnv = typeof process !== 'undefined' && process.env
      ? (process.env['NODE_ENV'] || 'production')
      : 'production';

    let platform: Environment['platform'] = 'unknown';
    if (this._isNode) platform = 'node';
    else if (this._isBrowser && !this._isVSCode) platform = 'browser';
    else if (this._isVSCode) platform = 'vscode';
    else if (this._isStandalone) platform = 'standalone';

    return {
      isDevelopment: nodeEnv === 'development',
      isProduction: nodeEnv === 'production',
      nodeEnv,
      platform,
      arch: typeof process !== 'undefined' ? process.arch : 'x64'
    };
  }
}

// Singleton instance
let _instance: EnvironmentDetector | null = null;

export function getEnvironmentDetector(): EnvironmentDetector {
  if (!_instance) {
    _instance = new EnvironmentDetector();
  }
  return _instance;
}